/**
 * Karsten-based source counting and target computation.
 *
 * Pure functions — no React, no store dependencies.
 * Architecture AD-062, AD-063, AD-071.
 */
import { isLand } from '../utils/card-helpers.js';
// ─── Named constants ──────────────────────────────────────────────────────────
export const PIP_SCALING_FACTOR = 6;
/** Recommended minimum land sources per color by color count. */
export const BASE_FLOORS = {
    1: 20,
    2: 20,
    3: 16,
    4: 15,
    5: 15,
};
/** Strict minimum (absolute floor) land sources per color by color count. */
export const STRICT_FLOORS = {
    1: 18,
    2: 18,
    3: 14,
    4: 13,
    5: 13,
};
export const ROCK_DISCOUNT = 0.75;
export const DORK_DISCOUNT = 0.5;
export const LAND_SEARCH_DISCOUNT = 0.75;
// ─── expandHybridPipCounts ────────────────────────────────────────────────────
/**
 * Expand hybrid and Phyrexian pip keys from parseManaRequirements output.
 *
 * Rules (AD-061):
 * - Pure color key "W" with count N → W += N
 * - Hybrid key "W/U" with count N → W += N*0.5, U += N*0.5
 * - Phyrexian hybrid "W/P" with count N → W += N*0.5 (cast for 2 life 50% of the time)
 *
 * Returns Map<Color, number> with fractional values.
 */
export function expandHybridPipCounts(requirements) {
    const result = new Map();
    const validColors = new Set(['W', 'U', 'B', 'R', 'G']);
    for (const [key, count] of requirements.entries()) {
        if (validColors.has(key)) {
            // Pure colored pip
            result.set(key, (result.get(key) ?? 0) + count);
        }
        else if (key.includes('/')) {
            const parts = key.split('/');
            const a = parts[0] ?? '';
            const b = parts[1] ?? '';
            if (b === 'P') {
                // Phyrexian: count as 0.5 of color a
                if (validColors.has(a)) {
                    result.set(a, (result.get(a) ?? 0) + count * 0.5);
                }
            }
            else if (validColors.has(a) && validColors.has(b)) {
                // Hybrid: split 0.5 each
                result.set(a, (result.get(a) ?? 0) + count * 0.5);
                result.set(b, (result.get(b) ?? 0) + count * 0.5);
            }
        }
        // Ignore generic, snow, colorless, etc.
    }
    return result;
}
// ─── computeKarstenTargets ────────────────────────────────────────────────────
/**
 * Compute per-color Karsten targets and strict floors from pip distribution.
 *
 * Formula (AD-063, PRD §3.2):
 *   baseFloor = BASE_FLOORS[colorCount]
 *   totalPips = sum of all pipCounts values
 *   colorPipShare[C] = pipCounts[C] / totalPips
 *   excessShare[C] = max(0, colorPipShare[C] - 1/colorCount)
 *   target[C] = baseFloor + round(excessShare[C] * PIP_SCALING_FACTOR)
 */
export function computeKarstenTargets(colorCount, pipCounts) {
    const clampedColorCount = Math.max(1, Math.min(5, colorCount));
    const baseFloor = BASE_FLOORS[clampedColorCount] ?? 15;
    const strictFloor = STRICT_FLOORS[clampedColorCount] ?? 13;
    const targets = new Map();
    const strictFloors = new Map();
    if (pipCounts.size === 0) {
        return { targets, strictFloors };
    }
    const totalPips = Array.from(pipCounts.values()).reduce((sum, v) => sum + v, 0);
    const equalShare = totalPips > 0 ? 1 / clampedColorCount : 0;
    for (const [color, pips] of pipCounts.entries()) {
        const colorPipShare = totalPips > 0 ? pips / totalPips : 0;
        const excessShare = Math.max(0, colorPipShare - equalShare);
        const target = baseFloor + Math.round(excessShare * PIP_SCALING_FACTOR);
        targets.set(color, target);
        strictFloors.set(color, strictFloor);
    }
    return { targets, strictFloors };
}
// ─── computeKarstenSources ────────────────────────────────────────────────────
/**
 * Count how many lands in `lands` can produce each color in `colorIdentity`.
 *
 * Rules:
 * - Rainbow/staple (_category==='rainbow'): counts for all identity colors.
 * - All others (including fetches): count by their producedColors.
 *   Fetches use their native 2 colors (e.g., Windswept Heath = W, G).
 *
 * @param lands - The lands to count sources for
 * @param colorIdentity - The deck's color identity
 */
export function computeKarstenSources(lands, colorIdentity) {
    const counts = new Map();
    for (const color of colorIdentity) {
        counts.set(color, 0);
    }
    const identitySet = new Set(colorIdentity);
    for (const land of lands) {
        const isRainbow = land._category === 'rainbow';
        if (isRainbow && land.producedColors.size === 0) {
            // True rainbow lands (Command Tower, City of Brass, etc.) have empty
            // producedColors in the catalog — they produce any color.
            // Count for all identity colors.
            for (const color of colorIdentity) {
                counts.set(color, (counts.get(color) ?? 0) + 1);
            }
        }
        else {
            // Everything else: count by producedColors only.
            // This includes fetches (native 2 colors), duals, basics,
            // and utility lands (Reliquary Tower has producedColors={} → counts for nothing).
            for (const color of land.producedColors) {
                if (identitySet.has(color)) {
                    counts.set(color, (counts.get(color) ?? 0) + 1);
                }
            }
        }
    }
    return counts;
}
// ─── computeNonLandSources ────────────────────────────────────────────────────
/**
 * Compute discounted non-land source contributions per color.
 *
 * Discount rates (AD-062):
 * - Mana rocks: ROCK_DISCOUNT (0.75) per color produced
 * - Mana dorks: DORK_DISCOUNT (0.5) per color produced
 * - Land-search ramp: LAND_SEARCH_DISCOUNT (0.75) per use
 *
 * Land-search ramp counts for all identity colors (it finds any color via fetching basics).
 */
export function computeNonLandSources(spells, colorIdentity) {
    const counts = new Map();
    for (const color of colorIdentity) {
        counts.set(color, 0);
    }
    const identitySet = new Set(colorIdentity);
    for (const spell of spells) {
        if (!spell.isManaProducer)
            continue;
        const producer = spell;
        let discount;
        switch (producer.producerType) {
            case 'dork':
                discount = DORK_DISCOUNT;
                break;
            case 'rock':
                discount = ROCK_DISCOUNT;
                break;
            case 'landSearch':
                discount = LAND_SEARCH_DISCOUNT;
                // Land search counts for all identity colors
                for (const color of colorIdentity) {
                    counts.set(color, (counts.get(color) ?? 0) + discount);
                }
                continue;
            default:
                // enchantment or other — treat as rock rate
                discount = ROCK_DISCOUNT;
        }
        for (const color of producer.producedColors) {
            if (identitySet.has(color)) {
                counts.set(color, (counts.get(color) ?? 0) + discount);
            }
        }
    }
    return counts;
}
// ─── buildLandMultiset ────────────────────────────────────────────────────────
/**
 * Build a LandMultiset (name → quantity) from deck entries.
 * Filters to land cards only (not commanders), sums quantities.
 */
export function buildLandMultiset(entries) {
    const multiset = new Map();
    for (const entry of entries) {
        if (entry.isCommander)
            continue;
        if (!isLand(entry.card))
            continue;
        multiset.set(entry.card.name, (multiset.get(entry.card.name) ?? 0) + entry.quantity);
    }
    return multiset;
}
//# sourceMappingURL=karsten-sources.js.map